<template>
  <div class="relative inline-block text-left">
    <!-- Button mit Tooltip -->
    <button 
      :aria-label="tooltipText || ''"
      type="button"
      @click="toggleDropdown"
      ref="dropdownButton"
      class="w-12 sm:w-20 h-20 rounded-tl-xl inline-flex group relative
            text-base font-normal
            text-skin-b-list-mute 
            hover:text-skin-b-list-base hover:bg-slate-300/40
            disabled:opacity-50 disabled:hover:bg-skin-active128 
            focus:outline-none focus:ring-0 
            transition duration-300 ease-in-out">
            
      <!-- Tooltip Element -->
      <span v-if="tooltipText" :class="tooltipClasses"
            class="opacity-0 group-hover:opacity-100
            transition-opacity duration-300 delay-[0ms] group-hover:delay-[2000ms]
            normal-case whitespace-nowrap
            bg-slate-800 px-2 py-1 text-xs text-slate-100 rounded-md absolute
            after:content-[''] after:absolute after:top-full
            after:border-4 after:border-transparent after:border-t-slate-800">
        {{ tooltipText }}
      </span>
      
      <!-- Slot for Button Content -->
      <div class="m-auto">
        <slot></slot>
      </div>
    </button>

    <!-- Dropdown-Menü -->
    <div
      v-if="isOpen"
      class="absolute mt-2 w-96 max-w-xs bg-white border-2 border-skin-b-list-bord rounded-md shadow-lg z-10 overflow-hidden"
    >
      <ul class="max-h-60 overflow-auto">
        <li
          v-for="(option, index) in this.filteredOptions"
          :key="option.value"
          @click="handleOptionClick(option)"
          @mouseenter="hoverIndex = index"
          @mouseleave="hoverIndex = -1"
          class="flex items-center px-4 py-2 cursor-pointer hover:bg-slate-300/40 text-skin-b-list-mute hover:text-skin-b-list-base"
          :class="{ 'bg-black/10': hoverIndex === index }"
        >
          <IconAddRfid
            v-if="option.icon === 'IconAddRfid'"
            class="w-6 h-6 mr-2"
          />
          <IconLearnRfid
            v-if="option.icon === 'IconLearnRfid'"
            class="w-6 h-6 mr-2"
          />
          <span>{{ option.label }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import IconAddRfid from "@/components/icons/IconAddRfid.vue";
import IconLearnRfid from "@/components/icons/IconLearnRfid.vue";

export default {
  name: "MyButtonWithDropdown",
  components: {
    IconAddRfid,
    IconLearnRfid,
  },
  props: {
    tooltipText: {
      type: String,
      default: "",
    },
    tooltipPosition: {
      type: String,
      default: "top-center",
      validator: (value) => ["top-left", "top-center", "top-right"].includes(value),
    },
    options: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  setup(props) {
    const isOpen = ref(false);
    const hoverIndex = ref(-1);
    const dropdownButton = ref(null);

    const filteredOptions = computed(() =>
      props.options.filter((option) => option.show)
    );

    const toggleDropdown = () => {
      isOpen.value = !isOpen.value;
    };

    const closeDropdown = () => {
      isOpen.value = false;
    };

    const handleOptionClick = (option) => {
      if (typeof option.action === "function") {
        option.action(); // Ruft die Funktion auf, die in der Option definiert ist
      }
      closeDropdown(); // Schließt das Dropdown nach der Auswahl
    };

    const handleClickOutside = (event) => {
      if (
        dropdownButton.value &&
        !dropdownButton.value.contains(event.target)
      ) {
        closeDropdown();
      }
    };

    onMounted(() => {
      document.addEventListener("click", handleClickOutside);
    });

    onBeforeUnmount(() => {
      document.removeEventListener("click", handleClickOutside);
    });

    return {
      isOpen,
      hoverIndex,
      dropdownButton,
      filteredOptions,
      toggleDropdown,
      closeDropdown,
      handleOptionClick,
    };
  },
  computed: {
    tooltipClasses() {
      switch (this.tooltipPosition) {
        case "top-left":
          return "left-0 top-0 -translate-x-0 -translate-y-full after:left-4";
        case "top-center":
          return "left-1/2 top-0 -translate-x-1/2 -translate-y-full after:left-1/2";
        case "top-right":
          return "right-0 top-0 -translate-x-0 -translate-y-full after:right-4";
        default:
          return "left-1/2 top-0 -translate-x-1/2 -translate-y-full after:left-1/2";
      }
    },
  },
};
</script>

<style scoped>
</style>
